/**
 * Outreach tab — client for `/api/ta-action-board/outreach`.
 *
 * Nothing here talks to LinkedIn. The only LinkedIn interaction is the recruiter's own browser tab,
 * opened by `copyAndOpenComposer` in the view.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
import { reportGlobalError } from '../../../shared/utils/globalErrorReporter';
import type { BriefDoc, Campaign, OutreachCandidate, OutreachConfig, Playbook, RecruiterStyle, RoleBrief, RoleSearch, SearchRulesView, SendAsPerson, SendAsView, SenderFact, SourcingJob, WhereSetting } from '../types/taOutreach';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

const BASE = `${PROXY_URL}/api/ta-action-board/outreach`;

export class OutreachApiError extends Error {
  status: number;
  /** The whole error body — some refusals carry more than a message (see `BriefSourceProblem`). */
  body: Record<string, unknown>;
  constructor(message: string, status: number, body: Record<string, unknown> = {}) {
    super(message);
    this.name = 'OutreachApiError';
    this.status = status;
    this.body = body;
  }
}

/** Why a brief could not be built: no Lever posting AND no readable kick-off sheet. */
export interface BriefSourceProblem {
  message: string;
  kickoff: 'missing' | 'not_connected' | 'unreadable' | 'unknown';
  kickoffUrl: string | null;
  airtableUrl: string | null;
}

export function briefSourceProblem(err: unknown): BriefSourceProblem | null {
  // Read by shape, not `instanceof`: a hot-reloaded module is a second copy of the class, and the
  // box must not silently fall back to a toast because of it.
  const body = (err as { body?: Record<string, unknown> } | null)?.body;
  if (!(err instanceof Error) || !body || body.code !== 'no_brief_source') return null;
  const str = (v: unknown) => (typeof v === 'string' && /^https:\/\//.test(v) ? v : null);
  const k = body.kickoff;
  return {
    message: err.message,
    kickoff: k === 'missing' || k === 'not_connected' || k === 'unreadable' ? k : 'unknown',
    kickoffUrl: str(body.kickoffUrl),
    airtableUrl: str(body.airtableUrl),
  };
}

/**
 * Every status the outreach router answers with on purpose. `authenticatedFetch` throws its own
 * error on any non-2xx BEFORE the body can be read, which dropped `code` / `airtableUrl` /
 * `kickoff` on the floor and turned the brief's "no source" refusal into a bare toast (Oct 15 2026).
 * These are handled here instead, with the same global report `authenticatedFetch` would have made.
 */
const HANDLED_STATUSES = [400, 401, 403, 404, 409, 413, 422, 423, 502, 503];

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = init.method || 'GET';
  const res = await authenticatedFetch(`${BASE}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init.headers || {}) },
    suppressErrorStatuses: HANDLED_STATUSES,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message = body?.error || `Request failed (${res.status})`;
    reportGlobalError({ source: 'api_error', message: `${method.toUpperCase()} ${path}: ${message}`, statusCode: res.status });
    throw new OutreachApiError(message, res.status, body || {});
  }
  return body as T;
}

const json = (method: string, data?: unknown): RequestInit => ({
  method,
  ...(data === undefined ? {} : { body: JSON.stringify(data) }),
});

export const fetchOutreachConfig = () => call<OutreachConfig>('/config');

/** One role's list, or — with no posting — every role's (used to resolve a reminder's deep link). */
/** `roleTitle` only names the role on the test candidate a local test proxy adds; it filters nothing. */
export const fetchOutreach = (postingId?: string, roleTitle?: string) =>
  call<{ items: OutreachCandidate[]; brief: BriefDoc | null; testMode: boolean }>(
    postingId
      ? `/?postingId=${encodeURIComponent(postingId)}${roleTitle ? `&roleTitle=${encodeURIComponent(roleTitle)}` : ''}`
      : '/'
  );

/** The Google token (if any) lets the proxy read the kick-off sheet AS the recruiter. */
export const compileBrief = (postingId: string, gmailToken: string | null, force = false) =>
  call<{ brief: BriefDoc; cached: boolean }>('/brief', {
    ...json('POST', { postingId, force }),
    headers: gmailToken ? { 'X-Gmail-Token': gmailToken } : {},
  });

export const saveBrief = (
  postingId: string,
  patch: Partial<{
    brief: Partial<RoleBrief>;
    language: 'de' | 'en';
    followUpAfterDays: number;
    inmailTemplate: { scope: 'company' | 'personal'; key: string } | null;
    enrichmentEnabled: boolean;
    roleTitle: string;
    /** How the role works (step 1); null = as the brief says. */
    where: WhereSetting | null;
  }>,
  /** The version on screen: the brief is the role's, so a colleague's save in between is refused (409 BRIEF_CHANGED). */
  baseVersion: number
) => call<{ brief: BriefDoc }>(`/brief/${postingId}`, json('PUT', { ...patch, baseVersion }));

export const importOutreach = (postingId: string, roleTitle: string, csv: string) =>
  call<ImportResult & { truncated: boolean }>(
    '/import',
    json('POST', { postingId, roleTitle, csv })
  );

/**
 * Another template for this person from the parts already written — no model, well under a second
 * (Oct 22 2026). `needsRedraft`: the draft predates stored parts; redraft with `draftOutreach`.
 */
/** Why a template switch could not reuse the stored parts (Oct 27 2026). */
export type RedraftReason = 'no_parts' | 'no_brief' | 'no_template' | 'other_sender' | 'missing_part';
export const applyOutreachTemplate = (id: string, template: { scope: 'company' | 'personal'; key: string } | null) =>
  call<{ item?: OutreachCandidate; needsRedraft?: boolean; reason?: RedraftReason; refusal?: string | null }>(`/${id}/apply-template`, json('POST', { template }));

/**
 * A colleague's draft put together for the caller right before they send it (Oct 28 2026): no model,
 * an approval kept. `needsRedraft`: only a redraft can (e.g. common ground for another sender).
 */
export const signOutreachDraft = (id: string) =>
  call<{ item?: OutreachCandidate; needsRedraft?: boolean; reason?: RedraftReason; refusal?: string | null }>(`/${id}/sign`, json('POST', {}));

/**
 * `template`: a pick for this person (null = back to the role's); leave out to keep what they have.
 * `auto`: the auto-draft (Nov 2 2026) — a draft without a warning comes back approved (`ready`,
 * `autoApproved`), one with a warning `drafted` with `autoHeld`; a row drafted meanwhile is `skipped`.
 */
export const draftOutreach = (id: string, template?: { scope: 'company' | 'personal'; key: string } | null, { auto = false }: { auto?: boolean } = {}) =>
  call<{ item: OutreachCandidate; refusal: string | null; error: string | null; skipped?: boolean }>(
    `/${id}/draft`,
    json('POST', { ...(template === undefined ? {} : { template }), ...(auto ? { auto: true } : {}) })
  );

export const refineOutreach = (id: string, subject: string, body: string, instruction: string) =>
  call<{ ok: boolean; subject?: string; body?: string; warnings?: string[]; refusal?: string }>(
    `/${id}/refine`,
    json('POST', { subject, body, instruction })
  );

export const saveOutreachDraft = (id: string, subject: string, body: string) =>
  call<{ item: OutreachCandidate; refusal: string | null }>(`/${id}`, json('PATCH', { subject, body }));

/** The follow-up text for LinkedIn's own "Follow-up-Nachricht"; '' = back to the standard text. */
/** "Remove salary" (with the text on screen) or "Add salary back" on one InMail. */
export const setOutreachSalary = (id: string, include: boolean, text?: { subject: string; body: string }) =>
  call<{ item: OutreachCandidate; refusal?: string | null }>(`/${id}/salary`, json('POST', { include, ...(text || {}) }));

export const saveFollowUpText = (id: string, text: string) => call<{ item: OutreachCandidate }>(`/${id}/follow-up-text`, json('PATCH', { text }));

export const setExcludedFacts = (id: string, excludedFactIds: string[]) =>
  call<{ item: OutreachCandidate }>(`/${id}/facts`, json('PATCH', { excludedFactIds }));

/** Set the Recruiter id from a pasted Recruiter profile link (or the bare id). */
export const setRecruiterLink = (id: string, recruiterUrl: string) =>
  call<{ item: OutreachCandidate }>(`/${id}/recruiter`, json('PATCH', { recruiterUrl }));

export const markOutreachSent = (id: string, subject: string, body: string, followUp?: { inLinkedIn: boolean; text: string; days?: number | null }) =>
  call<{ item: OutreachCandidate; testMode: boolean }>(
    `/${id}/sent`,
    json('POST', {
      subject,
      body,
      ...(followUp?.inLinkedIn ? { followUpInLinkedIn: true, followUpText: followUp.text, ...(followUp.days ? { followUpDays: followUp.days } : {}) } : {}),
    })
  );

/** Read what Lever knows about this role's sent InMails (RSC stage changes). GET only on the proxy side. */
export const syncLever = (postingId: string, force = false) =>
  call<{ items: OutreachCandidate[]; available: boolean; throttled?: boolean; error?: string; checkedAt?: string }>('/sync', json('POST', { postingId, force }));

/** A LinkedIn notification mail the proxy could not place on a candidate. Short preview only. */
export interface ReplyNotification {
  threadId: string | null;
  messageId: string | null;
  at: string | null;
  subject: string;
  preview: string;
  gmailUrl: string | null;
  ambiguous: string[] | null;
}

/** Replies from LinkedIn's notification mails in the recruiter's own Gmail (read only). */
export const syncReplyMail = (postingId: string, gmailToken: string, force = false) =>
  call<{ items: OutreachCandidate[]; unmatched: ReplyNotification[]; connected: boolean; throttled?: boolean; checkedAt?: string }>(
    '/sync-mail',
    { ...json('POST', { postingId, force }), headers: { 'X-Gmail-Token': gmailToken } }
  );

export const placeReply = (id: string, reply: { gmailThreadId?: string | null; preview?: string | null; at?: string | null }) =>
  call<{ item: OutreachCandidate }>(`/${id}/reply`, json('POST', reply));

export const ignoreReplyMail = (postingId: string, threadId: string) => call<{ ok: true }>('/sync-mail/ignore', json('POST', { postingId, threadId }));

/** The recruiter sent the follow-up themself: closes it and counts it (tracking, Oct 26 2026). */
export const markFollowUpSent = (id: string) => call<{ item: OutreachCandidate }>(`/${id}/follow-up/sent`, json('POST'));
export const closeFollowUp = (id: string, reason: 'replied' | 'no_reply' | 'manual') =>
  call<{ item: OutreachCandidate }>(`/${id}/follow-up/close`, json('POST', { reason }));

export const dropOutreach = (id: string) => call<{ item: OutreachCandidate }>(`/${id}/drop`, json('POST', {}));

/** `updated`: people already on the list who were missing their Recruiter id and now have it. */
export type ImportResult = {
  imported: number;
  skipped: { row: number | null; reason: string }[];
  items: OutreachCandidate[];
  updated?: OutreachCandidate[];
};

/** A Recruiter "Save to PDF" ZIP → its PDFs (base64), so they can be imported one by one. */
export const unzipOutreach = (data: string) => call<{ files: { name: string; data: string }[] }>('/unzip', json('POST', { data }));

/** One Recruiter profile PDF. The file name carries the Recruiter id, so it is sent too. */
export const importOutreachPdf = (postingId: string, roleTitle: string, name: string, data: string) =>
  call<ImportResult>('/import-pdf', json('POST', { postingId, roleTitle, name, data }));

/** Browser File → base64 without a data: prefix (chunked, so large files do not overflow the stack). */
export async function fileToBase64(file: Blob): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

export const fetchCampaigns = () => call<{ campaigns: Campaign[] }>('/campaigns');

/** The profile check alone — nothing is drafted until the recruiter picks someone. */
export const analyseOutreach = (id: string) => call<{ item: OutreachCandidate }>(`/${id}/analyse`, json('POST', {}));

export type OutreachMove = 'queue' | 'ready' | 'unready' | 'not_fit' | 'skip' | 'resend_test' | 'restore';
/** `resigned` (Approve on a colleague's draft, Oct 28 2026): put together as the caller with a new text, still "drafted". */
export const moveOutreach = (id: string, move: OutreachMove) => call<{ item: OutreachCandidate; resigned?: boolean }>(`/${id}/move`, json('POST', { move }));

/** One id of a batch call: its row, or why that one was refused (the others still went through). */
export type ManyResult<Extra = object> =
  | ({ id: string; item: OutreachCandidate; status?: undefined; error?: undefined } & Extra)
  | { id: string; item?: undefined; status: number; error: string; code?: string; reason?: string | null };

/** "Approve N" as one request (Nov 2 2026): each id exactly as `moveOutreach(id, 'ready')`. */
export const approveMany = (ids: string[]) =>
  call<{ results: ManyResult<{ resigned?: boolean }>[] }>('/move-many', json('POST', { ids, move: 'ready' }));

// ─── sourcing (Oct 2026) ───

/** Accept or reject someone the sourcing agent found (no "hold" since Oct 19 2026). */
export const decideShortlisted = (id: string, decision: 'accept' | 'reject', reason?: string) =>
  call<{ item: OutreachCandidate }>(`/${id}/decide`, json('POST', { decision, reason }));

/**
 * Accept many on the shortlist as one request (Nov 2 2026): each id as `decideShortlisted`; an
 * accepted person whose profile is read comes back queued (not drafted — Write & send drafts by
 * itself), or with `queueError` when they cannot be queued (e.g. already reached for the role).
 */
export const acceptMany = (ids: string[]) =>
  call<{ results: ManyResult<{ queueError?: { status: number; error: string; code?: string } }>[] }>('/decide-many', json('POST', { ids, decision: 'accept' }));

/** What the extension read off a Recruiter profile page. */
export interface CapturedProfile {
  url: string;
  text: string;
  name?: string | null;
  headline?: string | null;
  location?: string | null;
  publicUrl?: string | null;
  /** How many collapsed sections the reader opened, and how many it left (extension 0.6.0). */
  expanded?: number;
  markersLeft?: number;
}

/** Store the profile text (and the Recruiter id LinkedIn answered to) on a candidate. */
export const saveCapture = (id: string, p: CapturedProfile) => call<{ item: OutreachCandidate }>(`/${id}/capture`, json('POST', p));

/** Add the profile open in Recruiter to this role, through the same checks as any import. */
export const importCapture = (postingId: string, roleTitle: string, p: CapturedProfile) =>
  call<ImportResult>('/capture', json('POST', { postingId, roleTitle, ...p }));

/** Right before a send: was this person contacted by anyone in the last six months? 409 = yes. */
export const precheckSend = (id: string) => call<{ ok: true }>(`/${id}/precheck`, json('POST', {}));

export const fetchSourcingJobs = (postingId: string) =>
  call<{ jobs: SourcingJob[] }>(`/sourcing/jobs?postingId=${encodeURIComponent(postingId)}`);

export const createSourcingJob = (job: {
  postingId: string;
  roleTitle: string;
  target: number;
  focus: string;
  exclude: string;
  maxProfiles: number;
  sourceUrl?: string;
  maxScreened?: number;
  /** Stage 2: run this search in the project first (`sourceUrl` = any page of the project). */
  searchKeywords?: string;
}) => call<{ job: SourcingJob }>('/sourcing/jobs', json('POST', job));

/** The role's Recruiter search, built from the brief (Stage 2, Oct 20 2026). */
type SearchView = { search: RoleSearch | null; stale: boolean; cached?: boolean; recruiterProjectId?: string | null };
export const fetchRoleSearch = (postingId: string) => call<SearchView>(`/sourcing/search?postingId=${encodeURIComponent(postingId)}`);
export const buildRoleSearch = (postingId: string, force = false) => call<SearchView>('/sourcing/search', json('POST', { postingId, force }));
/** `baseRev`: the search on screen — a colleague's change in between is refused (Oct 27 2026). */
export const saveRoleSearch = (postingId: string, boolean: string, baseRev?: number) =>
  call<SearchView>('/sourcing/search', json('PUT', { postingId, boolean, ...(baseRev === undefined ? {} : { baseRev }) }));
/** Stage 3 (Oct 22 2026): the search's learned rules — proposed by the agent, decided by a recruiter. */
export const fetchSearchRules = (postingId: string) => call<SearchRulesView>(`/sourcing/search/rules?postingId=${encodeURIComponent(postingId)}`);
export const proposeSearchRules = (postingId: string) => call<SearchRulesView>('/sourcing/search/rules/propose', json('POST', { postingId }));
export const challengeSearchRule = (postingId: string, ruleId: string) =>
  call<SearchRulesView>(`/sourcing/search/rules/${encodeURIComponent(ruleId)}/challenge`, json('POST', { postingId }));
export const decideSearchRule = (postingId: string, ruleId: string, decision: 'accept' | 'decline' | 'withdraw', confirm = false) =>
  call<SearchRulesView>(`/sourcing/search/rules/${encodeURIComponent(ruleId)}/decide`, json('POST', { postingId, decision, confirm }));

/** Who the extension saved to the project's LinkedIn pipeline (Oct 22 2026). */
export const recordPipelineSaves = (jobId: string, results: { recruiterId: string; status: string }[]) =>
  call<{ items: OutreachCandidate[] }>(`/sourcing/jobs/${jobId}/pipeline`, json('POST', { results }));

/** The search LinkedIn made from the job's keywords: the run reads it from now on. */
export const setJobSearch = (jobId: string, url: string, filters: string[]) =>
  call<{ job: SourcingJob }>(`/sourcing/jobs/${jobId}/search`, json('POST', { url, filters }));

/** The sourcing quality check: everyone a run looked at, with verdicts, quotes and the card text. */
export interface QualityExport {
  job: SourcingJob;
  brief: RoleBrief | null;
  rows: {
    page: number;
    position: number;
    name: string;
    recruiterUrl: string;
    outcome: string;
    reason: string | null;
    /** Why they are NOT on the shortlist now, with the evidence; null when they are. */
    whyNot: string | null;
    recommendation: 'talk_to' | 'needs_look' | 'skip' | null;
    listCriteria: { label: string; verdict: string; quote: string | null }[] | null;
    fullProfileRead: boolean;
    /** Collapsed sections the reader opened / left closed (extension 0.6.0); null for older reads. */
    sectionsOpened?: number | null;
    sectionsLeftClosed?: number | null;
    fullCriteria: { label: string; mustHave: boolean; verdict: string; quote: string | null }[] | null;
    status: string | null;
    decision: string | null;
    decisionReason: string | null;
    cardText: string | null;
  }[];
}

export const fetchSourcingExport = (jobId: string) => call<QualityExport>(`/sourcing/jobs/${jobId}/export`);

/** "Start sourcing": one page of Recruiter list cards (read by the extension) → screened onto the shortlist. */
export const screenSourcingPage = (jobId: string, start: number, cards: { recruiterId: string; href: string; text: string; name?: string | null }[]) =>
  call<{
    job: SourcingJob;
    items: OutreachCandidate[];
    page: { start: number; cards: number; contacted: number; blocked: number; known: number; screened: number; saved: number; misfits: number };
  }>(`/sourcing/jobs/${jobId}/page`, json('POST', { start, cards }));

export const cancelSourcingJob = (id: string) => call<{ job: SourcingJob }>(`/sourcing/jobs/${id}/cancel`, json('POST', {}));

export const fetchPlaybooks = () => call<{ playbooks: { sourcing: Playbook; outreach: Playbook } }>('/sourcing/playbooks');

export const savePlaybook = (id: 'sourcing' | 'outreach', markdown: string, baseVersion: number) =>
  call<{ playbook: Playbook }>(`/sourcing/playbooks/${id}`, json('PUT', { markdown, baseVersion }));

export const fetchStyle = () => call<{ style: RecruiterStyle }>('/sourcing/style');

export const saveStyle = (style: Partial<RecruiterStyle>) => call<{ style: RecruiterStyle }>('/sourcing/style', json('PUT', style));

// ─── InMail Library (Oct 19 2026) ───

export interface PlaybookVersion {
  version: number;
  by: string | null;
  at: string;
  markdown: string;
}
/** The InMail Library's standard subject; version 0 = the built-in default nobody has saved yet. */
export interface DefaultSubject {
  de: string;
  en: string;
  version: number;
  updatedBy: string | null;
  updatedAt: string | null;
}
export const fetchLibraryRules = () => call<{ fixed: string[]; team: Playbook; mine: string; subject: DefaultSubject }>('/library-rules');
export const saveDefaultSubject = (de: string, en: string, baseVersion: number) =>
  call<{ subject: DefaultSubject }>('/sourcing/subject', json('PUT', { de, en, baseVersion }));
export const fetchPlaybookHistory = (id: 'outreach') => call<{ history: PlaybookVersion[] }>(`/sourcing/playbooks/${id}/history`);
/** A sample InMail with the rules as they stand in the editor; nothing is saved. */
export const draftPreview = (id: string, rules: { teamRules?: string; myRules?: string }) =>
  call<{ draft: { subject: string; body: string; warnings: string[] } | null; refusal: string | null; error: string | null; language: 'de' | 'en'; profileRead: boolean }>(
    `/${id}/draft-preview`,
    json('POST', rules)
  );

/** Write & send › who sends and who takes the call (Oct 26 2026). */
export const fetchSendAs = () => call<SendAsView>('/send-as');
/** Saves the choice; open drafts that were not edited by hand are put together again for it. */
export const saveSendAs = (sendAs: string, interviewer: string, useCommonGround = true) =>
  call<{ choice: SendAsView['choice']; updated: number; needsYou: string[] }>('/send-as', json('PUT', { sendAs, interviewer, useCommonGround }));
/** Common ground › a sender's profile facts (PR 7, Oct 27 2026). */
export interface SenderProfile {
  facts: SenderFact[];
  readAt: string | null;
}
const senderPath = (email: string) => `/senders/${encodeURIComponent(email)}/profile`;
export const fetchSenderProfile = (email: string) => call<SenderProfile>(senderPath(email));
/** The profile text the extension read; the server keeps only the facts it checked. */
export const readSenderProfile = (email: string, text: string, pages?: Record<string, number>) =>
  call<SenderProfile>(senderPath(email), json('POST', { text, pages }));
export const saveSenderFacts = (email: string, facts: Array<{ id: string; on: boolean }>) => call<SenderProfile>(senderPath(email), json('PUT', { facts }));
export const addSender = (p: { firstName: string; lastName: string; email: string; linkedinUrl: string }) =>
  call<{ sender: SendAsPerson }>('/senders', json('POST', p));
/** Makes (or reopens) the screening link of whoever takes the call, with the Interview Links defaults. */
export const createScreeningLink = (email: string) =>
  call<{ email: string; created: boolean; reactivated: boolean }>('/screening-link', json('POST', { email }));
/** What "Create screening link" would make — the defaults, or a closed link's own settings. */
export interface ScreeningLinkPreview {
  email: string;
  state: 'new' | 'closed' | 'open';
  minutes: number;
  minNoticeHours: number;
  offeredRangeDays: number;
  timezone: string;
  hours: string[];
}
export const previewScreeningLink = (email: string) => call<ScreeningLinkPreview>(`/screening-link?email=${encodeURIComponent(email)}`);
